import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MissionHistory = ({ missions }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filteredMissions = missions?.filter(mission => {
    if (filter === 'all') return true;
    return mission?.status === filter;
  });

  const sortedMissions = [...filteredMissions]?.sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.completedAt) - new Date(a.completedAt);
    } else if (sortBy === 'performance') {
      return b?.score - a?.score;
    }
    return a?.title?.localeCompare(b?.title);
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { icon: 'CheckCircle', color: 'var(--color-success)' };
      case 'failed':
        return { icon: 'XCircle', color: 'var(--color-error)' };
      case 'in-progress':
        return { icon: 'Clock', color: 'var(--color-warning)' };
      default:
        return { icon: 'Circle', color: 'var(--color-muted-foreground)' };
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'text-success';
      case 'medium':
        return 'text-warning';
      case 'hard':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-subtle p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold text-foreground">Mission History</h2>
        
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e?.target?.value)}
            className="px-3 py-2 bg-input border border-subtle rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="in-progress">In Progress</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 bg-input border border-subtle rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="recent">Most Recent</option>
            <option value="performance">Best Performance</option>
            <option value="alphabetical">Alphabetical</option>
          </select>
        </div>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {sortedMissions?.map((mission) => {
          const statusInfo = getStatusIcon(mission?.status);
          
          return (
            <div
              key={mission?.id}
              className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0">
                <Icon name={statusInfo?.icon} size={20} color={statusInfo?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-foreground truncate">{mission?.title}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full bg-muted ${getDifficultyColor(mission?.difficulty)}`}>
                    {mission?.difficulty}
                  </span>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>
                    {new Date(mission.completedAt)?.toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    {mission?.executionTime}ms
                  </span>
                  {mission?.score && (
                    <span className="flex items-center gap-1">
                      <Icon name="Star" size={14} />
                      {mission?.score}/100
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {mission?.status === 'completed' && (
                  <div className="text-right">
                    <div className="text-sm font-medium text-success">
                      +{mission?.xpEarned} XP
                    </div>
                  </div>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="right"
                  onClick={() => window.open(`/mission-workspace?id=${mission?.id}`, '_blank')}
                >
                  View
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {sortedMissions?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">No missions found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default MissionHistory;